import { useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import qs from 'qs';

import { OnPageChangeType } from 'types/pagination';
import { ISelectOption } from 'types/select';
import { AllRoutes } from 'constants/routes';
import { IColumnSortingInfo } from 'types/table';
import { FILTER_GROUP_BY_PATH } from 'constants/filters';
import { getColumnsSorting } from 'helpers/getColumnSorting';
import { DefaultColumnsSorting } from 'constants/columns-sorting';
import { getModifiedRequestFilters } from 'helpers/getModifiedRequestFilters';
import { getParsedSearchParams } from '../helpers/getParsedSearchParams';

export const useEntitiesTable = (path: AllRoutes) => {
  const currentLocation = useLocation();
  const { push } = useHistory();

  const searchParams = qs.parse(currentLocation.search, {
    ignoreQueryPrefix: true,
  });
  const { page, pageSize, sort, tab, search, ...rest } = searchParams;

  const filters = qs.stringify(
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    { filter: getModifiedRequestFilters(FILTER_GROUP_BY_PATH[path], rest) },
    { arrayFormat: 'repeat' },
  );

  const navigationPayload = getParsedSearchParams(searchParams);

  const columnsSorting = useMemo(() => {
    const sort = navigationPayload.find(([key, value]) => key === 'sort')?.[1];

    return getColumnsSorting(DefaultColumnsSorting[path], sort);
  }, [searchParams]);

  const updatePage: OnPageChangeType = ({ selected }) => {
    const filteredParams = navigationPayload.filter(([key]) => key !== 'page');
    const updatedParams = [...filteredParams, ['page', String(selected)]];

    const paramsString = new URLSearchParams(updatedParams).toString();

    push(`${path}?${paramsString}`);
  };

  const updatePageSize = (selectedPageSize: ISelectOption<number>) => {
    const filteredParams = navigationPayload.filter(([key]) => key !== 'page' && key !== 'pageSize');
    const updatedParams = [...filteredParams, ['page', '1'], ['pageSize', String(selectedPageSize.value)]];
    const paramsString = new URLSearchParams(updatedParams).toString();

    push(`${path}?${paramsString}`);
  };

  const sortColumnData = (payload: IColumnSortingInfo) => {
    const filteredParams = navigationPayload.filter(([key]) => key !== 'page' && key !== 'sort');
    const updatedSort = payload?.sortBy ? [['sort', `${payload.id}:${payload.sortBy}`]] : [];
    const updatedParams = [...filteredParams, ['page', '1'], ...updatedSort];
    const paramsString = new URLSearchParams(updatedParams).toString();

    push(`${path}?${paramsString}`);
  };

  const searchHandler = (payload: string) => {
    const filteredParams = navigationPayload.filter(([key]) => key !== 'page' && key !== 'search');
    const updatedSearch = payload ? [['search', payload]] : [];
    const updatedParams = [...filteredParams, ['page', '1'], ...updatedSearch];
    const paramsString = new URLSearchParams(updatedParams).toString();

    push(`${path}?${paramsString}`);
  };

  return {
    page: page ? Number(page) : 1,
    pageSize: { value: pageSize ? Number(pageSize) : 10, label: 'display X entries' },
    sort: sort ? String(sort) : undefined,
    tab: tab ? String(tab) : undefined,
    search: search ? String(search) : null,
    searchParams: currentLocation.search,
    filters,
    columnsSorting,
    updatePage,
    updatePageSize,
    sortColumnData,
    searchHandler,
  };
};
